import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addItem } from "../redux/action/index";
import "./Order.css";
import {
  FiPackage,
  FiXCircle,
  FiStar,
  FiChevronDown,
  FiChevronUp,
  FiShoppingCart,
} from "react-icons/fi";
import { sendRequest } from "../ApiRequest";
import { sendCachedRequest } from "../utils/apiCache";
import { BASE_URL } from "../URL/constants";
import useCurrentUser from "../components/common/UseCurrentUser";

const Order = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [statusFilters, setStatusFilters] = useState({
    onTheWay: false,
    delivered: false,
    cancelled: false,
    returned: false,
  });
  const [timeFilters, setTimeFilters] = useState({
    last30Days: false,
    year2024: false,
    year2023: false,
    older: false,
  });
  const [businessType, setBusinessType] = useState(null);
  const [businessCategory, setBusinessCategory] = useState(null);
  const [businessInfoLoaded, setBusinessInfoLoaded] = useState(false);
  const { currentUser, isLoggedIn } = useCurrentUser();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [authChecked, setAuthChecked] = useState(false);

  // Check authentication - redirect if not logged in
  useEffect(() => {
    // Wait a bit for useCurrentUser hook to initialize
    const checkAuth = () => {
      const token = localStorage.getItem("authToken");
      const userDetailsStr = localStorage.getItem("userDetails");
      
      if (!token || !userDetailsStr) {
        navigate("/");
        return;
      }
      
      try {
        const userDetails = JSON.parse(userDetailsStr);
        // Check if user is actually logged in by checking the data structure
        if (!userDetails || !userDetails.data || !userDetails.data.customer || !userDetails.data.customer.id) {
          navigate("/");
          return;
        }
        
        // User is authenticated
        setAuthChecked(true);
      } catch (error) {
        console.error("Error parsing userDetails:", error);
        navigate("/");
      }
    };
    
    // Small delay to ensure useCurrentUser hook has initialized
    const timer = setTimeout(checkAuth, 100);
    return () => clearTimeout(timer);
  }, [navigate]);
  
  // Check if design should be applied (retail + groceries/cosmetic/general_business)
  const shouldApplyDesign = businessType === "retail" && 
    (businessCategory === "groceries" || businessCategory === "cosmetic" || businessCategory === "general_business");

  // Fetch business type and category
  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await sendCachedRequest(
          'POST',
          `${BASE_URL}/v1/public_site/public_site_info/get_list`,
          null,
          { cacheKey: 'public_site_info' }
        );
        
        const apiBusinessType = response?.data?.data?.business_type;
        const apiBusinessCategory = response?.data?.data?.business_category;
        
        setBusinessType(apiBusinessType);
        setBusinessCategory(apiBusinessCategory);
        setBusinessInfoLoaded(true);
      } catch (error) {
        console.error('Error fetching business info:', error);
        setBusinessInfoLoaded(true); // Still set to true to prevent infinite loading
      }
    };

    fetchBusinessInfo();
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("authToken");
        if (!token) {
          throw new Error("Unauthorized customer!");
        }

        const response = await sendRequest(
          "GET",
          `${BASE_URL}/v1/public_site/order_payments/customer_orders`,
          null,
          {
            Authorization: token,
          }
        );

        // Safely handle case when there are no orders for the user
        const apiOrders = Array.isArray(response?.data?.orders)
          ? response.data.orders
          : [];

        const transformedOrders = apiOrders.map((order) => {
          // Transform items to products format
          const products = order.items
            ? order.items.map((item) => {
                // Handle item_image_url - can be string, array of strings, or array of objects
                let imageUrl = null;
                if (item.item_image_url) {
                  if (typeof item.item_image_url === 'string') {
                    imageUrl = item.item_image_url;
                  } else if (Array.isArray(item.item_image_url)) {
                    // Flatten nested arrays and extract URLs
                    const flattened = item.item_image_url.flat(Infinity);
                    const firstImage = flattened.find(img => {
                      if (typeof img === 'string') return img;
                      if (img && typeof img === 'object') {
                        return img.image_url || img.imageUrl || img.url;
                      }
                      return null;
                    });
                    if (typeof firstImage === 'string') {
                      imageUrl = firstImage;
                    } else if (firstImage && typeof firstImage === 'object') {
                      imageUrl = firstImage.image_url || firstImage.imageUrl || firstImage.url;
                    }
                  }
                }
                
                return {
                  id: item.id || `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                  item_id: item.id, // Keep original item_id for reorder
                  name: item.item_name?.en || item.item_name || "Unknown Product",
                  price: parseFloat(item.price) || 0,
                  quantity: item.quantity || 1,
                  image: imageUrl || item.image_url || item.image || item.item_image || null,
                  color: item.color || item.item_color || null,
                  size: item.size || item.item_size || null,
                  total_price: parseFloat(item.total_price) || parseFloat(item.price) || 0,
                };
              })
            : [];
          
          const transformedOrder = {
            id: order.id || order.order_id || `order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            orderId: order.id, // Keep original order ID
            date: order.created_at || order.order_date || new Date().toISOString(),
            status: order.status || "pending",
            itemsCount: order.items?.length || products.length || 0,
            total: parseFloat(order.total_amount) || parseFloat(order.amount) || 0,
            payment: order.payment || {},
            products: products,
          };

          if (order.expected_delivery) {
            transformedOrder.deliveryDate = order.expected_delivery;
          }

          return transformedOrder;
        });

        setOrders(transformedOrders);
      } catch (err) {
        console.error("Error fetching orders:", err);

        const status = err?.status || err?.statusCode;
        const apiMessage =
          err?.data?.message ||
          (Array.isArray(err?.data?.errors) ? err.data.errors[0] : err?.data?.errors) ||
          err?.message ||
          "";

        // If backend says no orders / 404, treat it as empty list, not an error
        if (
          status === 404 ||
          /no\s+orders/i.test(apiMessage || "")
        ) {
          setOrders([]);
          setError(null);
        } else if (apiMessage === "Unauthorized customer!") {
          setError("Please login to view your orders");
        } else {
          setError("Failed to load orders. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const handleProductClick = (productId) => {
    navigate(`/products/${productId}`);
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleReorderItem = async (item) => {
    if (!isLoggedIn || !currentUser?.data?.customer) {
      console.log("Please login to reorder items");
      return;
    }

    try {
      const token = currentUser.data.token;
      const customerId = currentUser.data.customer.id;
      
      const cartData = {
        customer_id: customerId,
        item_id: item.item_id || item.id,
        quantity: item.quantity || 1
      };

      const response = await sendRequest(
        'POST',
        `${BASE_URL}/v1/public_site/customers/${customerId}/cart/cart_items`,
        cartData,
        { Authorization: token }
      );

      if (response.data) {
        dispatch(addItem({ ...item, quantity: item.quantity || 1 }));
        window.dispatchEvent(new Event('cart-updated'));
        alert(`${item.name} added to cart successfully!`);
      }
    } catch (error) {
      console.error("Error adding item to cart:", error);
      alert("Failed to add item to cart. Please try again.");
    }
  };

  const handleReorderAll = async (order) => {
    if (!isLoggedIn || !currentUser?.data?.customer) {
      console.log("Please login to reorder items");
      return;
    }

    if (!order.products || order.products.length === 0) {
      console.log("No items to reorder");
      return;
    }

    try {
      const token = currentUser.data.token;
      const customerId = currentUser.data.customer.id;
      
      // Add all items to cart
      const addPromises = order.products.map((item) => {
        const cartData = {
          customer_id: customerId,
          item_id: item.item_id || item.id,
          quantity: item.quantity || 1
        };
        return sendRequest(
          'POST',
          `${BASE_URL}/v1/public_site/customers/${customerId}/cart/cart_items`,
          cartData,
          { Authorization: token }
        );
      });

      await Promise.all(addPromises);
      
      // Add to Redux state
      order.products.forEach((item) => {
        dispatch(addItem({ ...item, quantity: item.quantity || 1 }));
      });
      
      window.dispatchEvent(new Event('cart-updated'));
      alert("All items added to cart successfully!");
      navigate('/cart');
    } catch (error) {
      console.error("Error adding items to cart:", error);
      alert("Failed to add some items to cart. Please try again.");
    }
  };

  const handleStatusFilterChange = (filterName) => {
    setStatusFilters((prev) => ({
      ...prev,
      [filterName]: !prev[filterName],
    }));
  };

  const handleTimeFilterChange = (filterName) => {
    setTimeFilters((prev) => ({
      ...prev,
      [filterName]: !prev[filterName],
    }));
  };

  const getFilteredOrders = () => {
    let filtered = orders;

    // Apply status filters
    const activeStatusFilters = Object.entries(statusFilters)
      .filter(([_, isActive]) => isActive)
      .map(([key]) => key);

    if (activeStatusFilters.length > 0) {
      filtered = filtered.filter((order) => {
        const status = order.status?.toLowerCase() || "";
        return activeStatusFilters.some((filter) => {
          switch (filter) {
            case "onTheWay":
              return status === "shipped" || status === "on the way" || status === "out for delivery";
            case "delivered":
              return status === "delivered" || status === "success";
            case "cancelled":
              return status === "cancelled" || status === "rejected" || status === "cancel";
            case "returned":
              return status === "returned" || status === "refunded";
            default:
              return false;
          }
        });
      });
    }

    // Apply time filters
    const activeTimeFilters = Object.entries(timeFilters)
      .filter(([_, isActive]) => isActive)
      .map(([key]) => key);
      
    if (activeTimeFilters.length > 0) {
      const now = new Date();
      filtered = filtered.filter((order) => {
        const orderDate = new Date(order.date);
        return activeTimeFilters.some((filter) => {
          switch (filter) {
            case "last30Days":
              const daysDiff = (now - orderDate) / (1000 * 60 * 60 * 24);
              return daysDiff <= 30;
            case "year2024":
              return orderDate.getFullYear() === 2024;
            case "year2023":
              return orderDate.getFullYear() === 2023;
            case "older":
              return orderDate.getFullYear() < 2023;
            default:
              return false;
        }
        });
      });
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((order) => {
        const orderIdMatch = String(order.id || order.orderId).toLowerCase().includes(query);
        const productMatch = order.products?.some((product) =>
          product.name?.toLowerCase().includes(query)
        );
        return orderIdMatch || productMatch;
      });
    }
          
    return filtered;
  };

  const filteredOrders = getFilteredOrders();

  const formatDate = (dateString) => {
    const options = { 
      day: "numeric", 
      month: "short", 
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    };
    return new Date(dateString).toLocaleString("en-IN", options);
  };

  const formatShortDate = (dateString) => {
    const options = { 
      day: "numeric", 
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    };
    return new Date(dateString).toLocaleString("en-IN", options);
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "/assets/images/products/iphone11.png";
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // If it's a relative path, construct the full URL
    if (imagePath.startsWith('/')) {
      return `${BASE_URL}${imagePath}`;
    }
    
    // If it's just a filename, assume it's in the products directory
    return `${BASE_URL}/assets/images/products/${imagePath}`;
  };


  // Don't render anything if authentication check is not complete or user is not logged in
  if (!authChecked) {
    return (
      <div className="order-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  // Double check - if still not authenticated, return null (redirect should have happened)
  const token = localStorage.getItem("authToken");
  const userDetailsStr = localStorage.getItem("userDetails");
  if (!token || !userDetailsStr) {
    return null;
  }
  
  try {
    const userDetails = JSON.parse(userDetailsStr);
    if (!userDetails || !userDetails.data || !userDetails.data.customer || !userDetails.data.customer.id) {
      return null;
    }
  } catch (error) {
    return null;
  }

  if (loading) {
    return (
      <div className="order-container">
        <div className="loading-spinner"></div>
        <p>Loading your orders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-container">
        <div className="error-message">
          <FiXCircle className="error-icon" />
          <p>{error}</p>
          <button
            className="retry-btn"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Wait for business info to load before rendering design
  if (!businessInfoLoaded) {
    return (
      <div className="order-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Render different design based on business type and category
  if (shouldApplyDesign) {
    // Retail + Groceries/Cosmetic/General Business Design
    return (
      <div className="flipkart-order-container retail-groceries-design">
        {/* Breadcrumbs */}
        <div className="breadcrumbs">
          <span onClick={() => navigate("/")} className="breadcrumb-link">Home</span>
          <span className="breadcrumb-separator"> &gt; </span>
          <span onClick={() => navigate("/")} className="breadcrumb-link">My Account</span>
          <span className="breadcrumb-separator"> &gt; </span>
          <span className="breadcrumb-current">My Orders</span>
        </div>

        <div className="flipkart-order-layout">
        {/* Left Sidebar - Filters */}
        <div className="order-sidebar">
          <div className="filter-section">
            <h3 className="filter-title">ORDER STATUS</h3>
            <div className="filter-options">
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={statusFilters.onTheWay}
                  onChange={() => handleStatusFilterChange("onTheWay")}
                />
                <span>On the way</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={statusFilters.delivered}
                  onChange={() => handleStatusFilterChange("delivered")}
                />
                <span>Delivered</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={statusFilters.cancelled}
                  onChange={() => handleStatusFilterChange("cancelled")}
                />
                <span>Cancelled</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={statusFilters.returned}
                  onChange={() => handleStatusFilterChange("returned")}
                />
                <span>Returned</span>
              </label>
            </div>
          </div>

          <div className="filter-section">
            <h3 className="filter-title">ORDER TIME</h3>
            <div className="filter-options">
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={timeFilters.last30Days}
                  onChange={() => handleTimeFilterChange("last30Days")}
                />
                <span>Last 30 days</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={timeFilters.year2024}
                  onChange={() => handleTimeFilterChange("year2024")}
                />
                <span>2024</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={timeFilters.year2023}
                  onChange={() => handleTimeFilterChange("year2023")}
                />
                <span>2023</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={timeFilters.older}
                  onChange={() => handleTimeFilterChange("older")}
                />
                <span>Older</span>
              </label>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="order-main-content">
          {/* Search Bar */}
          <div className="order-search-section">
            <div className="order-search-box">
              <input
                type="text"
                placeholder="Search your orders here"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="order-search-input"
              />
              <button className="order-search-btn">Search Orders</button>
            </div>
      </div>

          {/* Order List */}
      {filteredOrders.length === 0 ? (
        <div className="no-orders">
          <div className="no-orders-illustration">
            <FiPackage className="empty-icon" />
          </div>
          <h3>No orders found</h3>
              <p>You haven't placed any orders matching your filters yet.</p>
          <button
            className="shop-now-btn"
            onClick={() => navigate("/products")}
          >
            Continue Shopping
          </button>
        </div>
      ) : (
            <div className="flipkart-order-list">
              {filteredOrders.map((order) => {
                const isExpanded = expandedOrders.has(order.id);
                return (
                  <div key={order.id} className="flipkart-order-bill-card">
                    {/* Order Summary Header */}
                    <div 
                      className="flipkart-order-summary"
                      onClick={() => toggleOrderExpansion(order.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="order-summary-left">
                        <div className="order-id-section">
                          <span className="order-label">Order ID:</span>
                          <span className="order-id-value">#{order.orderId || order.id}</span>
                        </div>
                        <div className="order-date-section">
                          <span className="order-date">{formatShortDate(order.date)}</span>
                        </div>
                        <div className="order-items-count">
                          <span>{order.itemsCount || order.products?.length || 0} {order.itemsCount === 1 ? 'item' : 'items'}</span>
                        </div>
                      </div>
                      <div className="order-summary-right">
                        <div className="order-total-section">
                          <span className="order-total-label">Total:</span>
                          <span className="order-total-value">{formatCurrency(order.total)}</span>
                        </div>
                        <div className="order-status-badge">
                          <span className={`status-badge status-${order.status?.toLowerCase() || 'pending'}`}>
                            {order.status || 'Pending'}
                          </span>
                        </div>
                        <div className="order-expand-icon">
                          {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                        </div>
                      </div>
                    </div>

                    {/* Order Details - Expanded */}
                    {isExpanded && order.products && order.products.length > 0 && (
                      <div className="flipkart-order-details">
                        <div className="order-details-header">
                          <h4>Order Items</h4>
                          <button
                            className="reorder-all-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReorderAll(order);
                            }}
                          >
                            <FiShoppingCart />
                            Reorder All
                          </button>
                        </div>
                        <div className="order-items-list">
                          {order.products.map((product, index) => (
                            <div key={`${order.id}-${index}`} className="flipkart-order-item-card">
                              <div
                                className="flipkart-product-image"
                                onClick={() => handleProductClick(product.item_id || product.id)}
                              >
                                <img
                                  src={getImageUrl(product.image)}
                                  alt={product.name}
                                  onError={(e) => {
                                    e.target.src = "/assets/images/products/iphone11.png";
                                  }}
                                />
                              </div>
                              <div className="flipkart-product-details-section">
                                <h4
                                  className="flipkart-product-name"
                                  onClick={() => handleProductClick(product.item_id || product.id)}
                                >
                                  {product.name.length > 45
                                    ? `${product.name.substring(0, 45)}...`
                                    : product.name}
                                </h4>
                                <div className="flipkart-product-attributes">
                                  <span className="product-quantity">Qty: {product.quantity}</span>
                                  {product.color && (
                                    <span className="flipkart-product-attr">
                                      Color: {product.color}
                                    </span>
                                  )}
                                  {product.size && (
                                    <span className="flipkart-product-attr">
                                      Size: {product.size}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flipkart-product-price-section">
                                <p className="flipkart-product-price">
                                  {formatCurrency(product.price)}
                                </p>
                                <p className="product-total-price">
                                  Total: {formatCurrency(product.total_price || (product.price * product.quantity))}
                                </p>
                              </div>
                              <div className="flipkart-order-actions-section">
                                <button
                                  className="reorder-item-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReorderItem(product);
                                  }}
                                >
                                  <FiShoppingCart />
                                  Reorder
                                </button>
                                {(order.status?.toLowerCase() === "delivered" ||
                                  order.status?.toLowerCase() === "success") && (
                                  <button
                                    type="button"
                                    className="rate-review-link"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate("/ratings-review", {
                                        state: {
                                          product: product,
                                          orderId: order.id,
                                        },
                                      });
                                    }}
                                  >
                                    <FiStar className="star-icon-small" />
                                    Rate & Review
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="order-summary-footer">
                          <div className="order-footer-info">
                            <span>Order Date: {formatDate(order.date)}</span>
                            {order.payment?.status && (
                              <span>Payment: {order.payment.status}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
    );
  }

  // Default Design (for other business types/categories)
  return (
    <div className="order-container">
      {/* Breadcrumbs */}
      <div className="breadcrumbs">
        <span onClick={() => navigate("/")} className="breadcrumb-link">Home</span>
        <span className="breadcrumb-separator"> &gt; </span>
        <span onClick={() => navigate("/")} className="breadcrumb-link">My Account</span>
        <span className="breadcrumb-separator"> &gt; </span>
        <span className="breadcrumb-current">My Orders</span>
      </div>

      <div className="order-layout">
        {/* Left Sidebar - Filters */}
        <div className="order-sidebar">
          <div className="filter-section">
            <h3 className="filter-title">ORDER STATUS</h3>
            <div className="filter-options">
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={statusFilters.onTheWay}
                  onChange={() => handleStatusFilterChange("onTheWay")}
                />
                <span>On the way</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={statusFilters.delivered}
                  onChange={() => handleStatusFilterChange("delivered")}
                />
                <span>Delivered</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={statusFilters.cancelled}
                  onChange={() => handleStatusFilterChange("cancelled")}
                />
                <span>Cancelled</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={statusFilters.returned}
                  onChange={() => handleStatusFilterChange("returned")}
                />
                <span>Returned</span>
              </label>
            </div>
          </div>

          <div className="filter-section">
            <h3 className="filter-title">ORDER TIME</h3>
            <div className="filter-options">
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={timeFilters.last30Days}
                  onChange={() => handleTimeFilterChange("last30Days")}
                />
                <span>Last 30 days</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={timeFilters.year2024}
                  onChange={() => handleTimeFilterChange("year2024")}
                />
                <span>2024</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={timeFilters.year2023}
                  onChange={() => handleTimeFilterChange("year2023")}
                />
                <span>2023</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={timeFilters.older}
                  onChange={() => handleTimeFilterChange("older")}
                />
                <span>Older</span>
              </label>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="order-main-content">
          {/* Search Bar */}
          <div className="order-search-section">
            <div className="order-search-box">
              <input
                type="text"
                placeholder="Search your orders here"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="order-search-input"
              />
              <button className="order-search-btn">Search Orders</button>
            </div>
          </div>

          {/* Order List */}
          {filteredOrders.length === 0 ? (
            <div className="no-orders">
              <div className="no-orders-illustration">
                <FiPackage className="empty-icon" />
              </div>
              <h3>No orders found</h3>
              <p>You haven't placed any orders matching your filters yet.</p>
              <button
                className="shop-now-btn"
                onClick={() => navigate("/products")}
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="order-list">
              {filteredOrders.map((order) => {
                const isExpanded = expandedOrders.has(order.id);
                return (
                  <div key={order.id} className="order-bill-card">
                    {/* Order Summary Header */}
                    <div 
                      className="order-summary-header"
                      onClick={() => toggleOrderExpansion(order.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="order-header-left">
                        <div className="order-id-display">
                          <span className="order-label">Order ID:</span>
                          <span className="order-id-value">#{order.orderId || order.id}</span>
                        </div>
                        <div className="order-meta">
                          <span className="order-date">{formatShortDate(order.date)}</span>
                          <span className="order-items-count">
                            {order.itemsCount || order.products?.length || 0} {order.itemsCount === 1 ? 'item' : 'items'}
                          </span>
                        </div>
                      </div>
                      <div className="order-header-right">
                        <div className="order-total-display">
                          <span className="order-total-label">Total:</span>
                          <span className="order-total-value">{formatCurrency(order.total)}</span>
                        </div>
                        <div className="order-status-display">
                          <span className={`status-badge status-${order.status?.toLowerCase() || 'pending'}`}>
                            {order.status || 'Pending'}
                          </span>
                        </div>
                        <div className="order-expand-icon">
                          {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                        </div>
                      </div>
                    </div>

                    {/* Order Details - Expanded */}
                    {isExpanded && order.products && order.products.length > 0 && (
                      <div className="order-details-expanded">
                        <div className="order-details-header">
                          <h4>Order Items</h4>
                          <button
                            className="reorder-all-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReorderAll(order);
                            }}
                          >
                            <FiShoppingCart />
                            Reorder All
                          </button>
                        </div>
                        <div className="order-items-container">
                          {order.products.map((product, index) => (
                            <div key={`${order.id}-${index}`} className="order-item-card">
                              <div
                                className="product-image"
                                onClick={() => handleProductClick(product.item_id || product.id)}
                              >
                                <img
                                  src={getImageUrl(product.image)}
                                  alt={product.name}
                                  onError={(e) => {
                                    e.target.src = "/assets/images/products/iphone11.png";
                                  }}
                                />
                              </div>
                              <div className="product-details-section">
                                <h4
                                  className="product-name"
                                  onClick={() => handleProductClick(product.item_id || product.id)}
                                >
                                  {product.name.length > 45
                                    ? `${product.name.substring(0, 45)}...`
                                    : product.name}
                                </h4>
                                <div className="product-attributes">
                                  <span className="product-quantity">Quantity: {product.quantity}</span>
                                  {product.color && (
                                    <span className="product-attr">
                                      Color: {product.color}
                                    </span>
                                  )}
                                  {product.size && (
                                    <span className="product-attr">
                                      Size: {product.size}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="product-price-section">
                                <p className="product-price">
                                  {formatCurrency(product.price)}
                                </p>
                                <p className="product-total-price">
                                  Total: {formatCurrency(product.total_price || (product.price * product.quantity))}
                                </p>
                              </div>
                              <div className="order-item-actions">
                                <button
                                  className="reorder-item-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReorderItem(product);
                                  }}
                                >
                                  <FiShoppingCart />
                                  Reorder
                                </button>
                                {(order.status?.toLowerCase() === "delivered" ||
                                  order.status?.toLowerCase() === "success") && (
                                  <button
                                    type="button"
                                    className="rate-review-link"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate("/ratings-review", {
                                        state: {
                                          product: product,
                                          orderId: order.id,
                                        },
                                      });
                                    }}
                                  >
                                    <FiStar className="star-icon-small" />
                                    Rate & Review
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="order-footer">
                          <div className="order-footer-info">
                            <span>Order Date: {formatDate(order.date)}</span>
                            {order.payment?.status && (
                              <span>Payment Status: {order.payment.status}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Order;
